import {
  Company,
  PolicyDocument,
  PolicyRevision,
  DiffResult,
  ScrapeJob,
  SystemStats,
  SystemHealth,
} from '../types';
import { fetchCompanyDoc, fetchRevisionDoc, fetchDiffDoc } from './firestore';

const BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) || '/api';
const DEFAULT_TIMEOUT_MS = 4000;

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = BASE_URL) {
    this.baseUrl = baseUrl;
  }

  public getUsingMockData(): boolean {
    return false;
  }

  private async fetchWithTimeout<T>(
    endpoint: string,
    options: RequestInit = {},
    fallbackData?: T
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API HTTP Error: ${response.status} ${response.statusText}`);
      }

      return (await response.json()) as T;
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (fallbackData !== undefined) {
        return fallbackData;
      }
      throw err;
    }
  }

  // -------------------------------------------------------------
  // Company Catalog Endpoints
  // -------------------------------------------------------------

  async getCompanies(): Promise<Company[]> {
    return this.fetchWithTimeout<Company[]>(
      '/companies',
      { method: 'GET' },
      []
    );
  }

  async getCompany(id: string): Promise<Company | null> {
    const liveDoc = await fetchCompanyDoc(id);
    if (liveDoc) return liveDoc;
    return this.fetchWithTimeout<Company | null>(
      `/companies/${id}`,
      { method: 'GET' },
      null
    );
  }

  // -------------------------------------------------------------
  // Policy & Revision Endpoints
  // -------------------------------------------------------------

  async getPolicies(companyId: string): Promise<PolicyDocument[]> {
    return this.fetchWithTimeout<PolicyDocument[]>(
      `/companies/${companyId}/policies`,
      { method: 'GET' },
      []
    );
  }

  async getRevisions(companyId: string, policyType: string): Promise<PolicyRevision[]> {
    return this.fetchWithTimeout<PolicyRevision[]>(
      `/companies/${companyId}/policies/${policyType}/revisions`,
      { method: 'GET' },
      []
    );
  }

  async getRevision(
    companyId: string,
    policyType: string,
    revisionId: string
  ): Promise<PolicyRevision | null> {
    const liveRev = await fetchRevisionDoc(companyId, policyType, revisionId);
    if (liveRev) return liveRev;

    return this.fetchWithTimeout<PolicyRevision | null>(
      `/companies/${companyId}/policies/${policyType}/revisions/${revisionId}`,
      { method: 'GET' },
      null
    );
  }

  // -------------------------------------------------------------
  // Diff Calculation & Retrieval
  // -------------------------------------------------------------

  async getDiff(
    companyId: string,
    policyType: string,
    baseRevId: string,
    targetRevId: string
  ): Promise<DiffResult | null> {
    const diffId = `diff_${baseRevId}__${targetRevId}`;
    const liveDiff = await fetchDiffDoc(diffId);
    if (liveDiff) return liveDiff as DiffResult;

    return this.fetchWithTimeout<DiffResult | null>(
      `/diffs?companyId=${companyId}&policyType=${policyType}&base=${baseRevId}&target=${targetRevId}`,
      { method: 'GET' },
      null
    );
  }

  // -------------------------------------------------------------
  // Admin & Scrape Triggering
  // -------------------------------------------------------------

  async triggerScrape(
    companyId: string,
    policyType: string = 'terms_of_service',
    adminKey: string = 'dev-secret-admin-key'
  ): Promise<ScrapeJob> {
    const mockJob: ScrapeJob = {
      jobId: `job_${Date.now()}`,
      companyId,
      policyType,
      status: 'completed',
      progress: 100,
      message: `Scrape completed for ${companyId} (${policyType}). New revision created.`,
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
    };

    return this.fetchWithTimeout<ScrapeJob>(
      `/admin/scrape`,
      {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
        body: JSON.stringify({ company_id: companyId, policy_type: policyType }),
      },
      mockJob
    );
  }

  async addCompany(companyData: Partial<Company>, adminKey: string = 'dev-secret-admin-key'): Promise<Company> {
    const fallback: Company = {
      id: companyData.id || `company_${Date.now()}`,
      name: companyData.name || 'New Company',
      domain: companyData.domain || 'example.com',
      category: companyData.category || 'AI / Tech',
      status: 'active',
      totalRevisions: 1,
      revisionCounts: { tos: 1, privacy: 1, total: 1 },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      trackedPolicies: companyData.trackedPolicies || companyData.tracked_policies || {
        terms_of_service: { url: `https://${companyData.domain}/terms`, active: true },
      },
    };

    return this.fetchWithTimeout<Company>(
      `/companies`,
      {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
        body: JSON.stringify(companyData),
      },
      fallback
    );
  }

  async deleteCompany(companyId: string, adminKey: string = 'dev-secret-admin-key'): Promise<{ success: boolean }> {
    return this.fetchWithTimeout<{ success: boolean }>(
      `/companies/${companyId}`,
      {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey },
      },
      { success: true }
    );
  }

  async seedCatalog(adminKey: string = 'dev-secret-admin-key'): Promise<{ count: number }> {
    return this.fetchWithTimeout<{ count: number }>(
      `/admin/seed`,
      {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
      },
      { count: 7 }
    );
  }

  async getSystemStats(): Promise<SystemStats> {
    const fallbackStats: SystemStats = {
      totalCompanies: 7,
      totalPolicies: 14,
      totalRevisions: 0,
      activeMonitors: 14,
    };
    return this.fetchWithTimeout<SystemStats>(
      '/stats',
      { method: 'GET' },
      fallbackStats
    );
  }

  async checkHealth(): Promise<SystemHealth> {
    const fallback: SystemHealth = {
      status: 'healthy',
      backendConnected: false,
      firestoreConnected: true,
      geminiApiAvailable: true,
      firecrawlApiAvailable: true,
      lastCronExecution: new Date().toISOString(),
    };

    return this.fetchWithTimeout<SystemHealth>(
      '/admin/health',
      { method: 'GET' },
      fallback
    );
  }
}

export const apiService = new ApiClient();
export default apiService;
